/**
 * Módulo de autenticação com Sync API usando fluxo OAuth 2.0 com PKCE.
 *
 * Documentação: https://backoffice.qa.id.syngenta.com/docs
 *
 * Responsabilidades:
 * - Iniciar fluxo de autorização (obtém código de autorização)
 * - Trocar código por tokens de acesso (implementa PKCE para segurança)
 * - Armazenar tokens de forma segura no armazenamento local
 */

import * as Sentry from '@sentry/nextjs';
import StorageService from '@/lib/storage/storage';
import { authConfig } from './config';
import {
  generateCodeChallenge,
  generateCodeVerifier,
  generateState,
} from './crypto';
import { requestToken } from './tokens';
import type { TokenResponse } from './types';

export async function initiateAuthFlow(userRef?: string): Promise<string> {
  const codeVerifier = generateCodeVerifier();
  const codeChallenge = await generateCodeChallenge(codeVerifier);
  const state = generateState();

  // Armazenar parâmetros PKCE para uso posterior
  StorageService.set(
    StorageService.Keys.ONBOARDING_AUTH_CODE_VERIFIER,
    codeVerifier,
  );
  StorageService.set(StorageService.Keys.ONBOARDING_AUTH_STATE, state);

  const authParams = new URLSearchParams({
    clientId: authConfig.clientId,
    codeChallenge: codeChallenge,
    codeChallengeMethod: 'S256',
  });

  if (userRef) authParams.append('userRef', userRef);

  Sentry.addBreadcrumb({
    category: 'sync initiate auth flow',
    message: 'sync initiate auth flow succeeded',
    level: 'info',
    data: { userRef, redirectUri: authConfig.redirectUri },
  });

  const response = await fetch(
    `${authConfig.baseUrl}/${authConfig.apiVersion}/auth/authorize?${authParams}`,
    {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    },
  );

  if (!response.ok) {
    // Sync answers some failures with a non-JSON body (e.g. the plain-text
    // "Too many requests" on HTTP 429). Parsing it unconditionally throws a
    // SyntaxError that masks the real status in Sentry.
    let detail = `status ${response.status}`;
    try {
      const error = await response.json();
      if (error?.error_description || error?.error) {
        detail = error.error_description || error.error;
      }
    } catch {
      /* non-JSON body */
    }
    throw new Error(`Authorization request failed: ${detail}`);
  }

  Sentry.addBreadcrumb({
    category: 'sync initiate auth flow',
    message: 'sync code generate success',
    level: 'info',
    data: { userRef },
  });

  const { code } = await response.json();
  return code;
}

export async function exchangeCodeForTokens(
  code: string,
  context?: 'exchange' | 'subscriber',
): Promise<TokenResponse> {
  const codeVerifier = StorageService.get(
    StorageService.Keys.ONBOARDING_AUTH_CODE_VERIFIER,
  ) as string;

  if (!codeVerifier) {
    throw new Error(
      'Code verifier not found. Please restart the authorization flow.',
    );
  }

  const tokenParams: Record<string, string> = {
    grantType: 'authorization_code',
    clientId: authConfig.clientId,
    code: code,
    codeVerifier,
  };

  Sentry.addBreadcrumb({
    category: 'sync initiate auth flow',
    message: 'sync code exchange succeeded',
    level: 'info',
    data: { grantType: 'authorization_code', clientId: authConfig.clientId },
  });

  const data = await requestToken(tokenParams, context);

  // Armazenar tokens de forma segura
  StorageService.set(
    StorageService.Keys.ONBOARDING_AUTH_ACCESS_TOKEN,
    data.access_token,
  );
  if (data.refresh_token) {
    StorageService.set(
      StorageService.Keys.ONBOARDING_AUTH_REFRESH_TOKEN,
      data.refresh_token,
    );
  }

  // Limpar parâmetros PKCE
  StorageService.remove(StorageService.Keys.ONBOARDING_AUTH_CODE_VERIFIER);

  return data;
}
